import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BrandStatsBar } from "@/components/BrandStatsBar";
import { BrandCatalogue } from "@/components/BrandCatalogue";
import { BrandTeam } from "@/components/BrandTeam";
import { toast } from "sonner";
import { Loader2, LogOut, Store, Globe, Mail, Phone, MapPin, ShieldCheck } from "lucide-react";
import { LOGO_LIGHT_URL } from "@/lib/brand";

export default function BrandDashboard() {
  const { user, authReady, logout } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const loadStats = () => api.get("/brand/stats").then((r) => setStats(r.data)).catch(() => {});

  useEffect(() => {
    if (authReady && (!user || user.role !== "brand")) { nav("/brand-login"); return; }
    if (authReady && user?.role === "brand") {
      api.get("/brand/me").then((r) => setData(r.data)).catch(() => nav("/brand-login"));
      loadStats();
      api.get("/brand/products").then((r) => setProducts(r.data)).catch(() => {});
    }
  }, [authReady, user, nav]);

  const changePw = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try { await api.post("/brand/change-password", { new_password: pw }); toast.success("Password updated"); setPw(""); setData((d) => ({ ...d, user: { ...d.user, must_change_password: false } })); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!authReady || !data) return <div className="min-h-screen flex justify-center items-center bg-cream"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>;

  const b = data.brand;
  const brandRole = data.user?.brand_role || "owner";
  const ROLE_LABELS = { owner: "Brand Owner / Primary Admin", catalogue: "Brand Catalogue User", viewer: "Brand Viewer" };
  const canEdit = brandRole !== "viewer";
  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-cream">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src={LOGO_LIGHT_URL} alt="LIVENEAT" className="h-14 w-auto object-contain" /><span className="text-[10px] tracking-widest text-mustard uppercase">Brand Portal</span></div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-cream/80 hidden sm:inline">{b.brand_name}</span>
            <Badge className="bg-mustard/30 text-cream hover:bg-mustard/30 border border-cream/20" data-testid="my-role-badge">{ROLE_LABELS[brandRole]}</Badge>
            <button onClick={() => { logout(); nav("/brand-login"); }} data-testid="brand-logout" className="flex items-center gap-2 text-sm text-cream/80 hover:text-cream"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 lg:px-8 py-8 space-y-6" data-testid="brand-dashboard">
        {/* Prominent stats — shown at the top of every Brand Portal page */}
        <BrandStatsBar stats={stats} />

        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white soft-shadow overflow-hidden flex items-center justify-center shrink-0">{b.logo ? <img src={b.logo} alt={b.brand_name} className="w-full h-full object-cover" /> : <Store className="w-6 h-6 text-sage" />}</div>
          <div>
            <h1 className="font-heading text-3xl text-forest">{b.brand_name}</h1>
            <div className="flex items-center gap-2 mt-1"><Badge className="bg-sage/25 text-forest hover:bg-sage/25">{b.status}</Badge>{b.legal_name && <span className="text-sm text-brown/60">{b.legal_name}</span>}</div>
          </div>
        </div>

        {data.user?.must_change_password && (
          <div className="bg-mustard/15 border border-mustard/40 rounded-2xl p-4">
            <p className="text-sm font-500 text-earth flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Set your own password</p>
            <p className="text-xs text-brown/70 mt-1 mb-3">You're using a temporary password. Please set a new one to secure your account.</p>
            <div className="flex gap-2 max-w-sm"><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="rounded-xl bg-white" data-testid="brand-newpw" /><Button onClick={changePw} disabled={saving} className="rounded-full bg-forest text-cream" data-testid="brand-savepw">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}</Button></div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl soft-shadow p-5 space-y-3">
            <h2 className="font-heading text-xl text-forest">Brand Profile</h2>
            {b.description && <p className="text-sm text-brown/80">{b.description}</p>}
            {b.website && <p className="text-sm flex items-center gap-2 text-brown"><Globe className="w-4 h-4 text-olive" /> {b.website}</p>}
            {b.contact_email && <p className="text-sm flex items-center gap-2 text-brown"><Mail className="w-4 h-4 text-olive" /> {b.contact_email}</p>}
            {b.contact_phone && <p className="text-sm flex items-center gap-2 text-brown"><Phone className="w-4 h-4 text-olive" /> {b.contact_phone}</p>}
            {b.address && <p className="text-sm flex items-start gap-2 text-brown"><MapPin className="w-4 h-4 text-olive mt-0.5" /> {b.address}</p>}
            {(b.operating_cities || []).length > 0 && <div className="flex flex-wrap gap-1.5 pt-1">{b.operating_cities.map((c) => <span key={c} className="text-xs bg-sand px-2 py-1 rounded-full text-brown">{c}</span>)}</div>}
          </div>
          <div className="bg-white rounded-2xl soft-shadow p-5 space-y-3">
            <h2 className="font-heading text-xl text-forest">Compliance & Catalogue</h2>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div><p className="text-[10px] uppercase text-olive">GST</p><p className="text-forest">{b.gst || "—"}</p></div>
              <div><p className="text-[10px] uppercase text-olive">PAN</p><p className="text-forest">{b.pan || "—"}</p></div>
              <div><p className="text-[10px] uppercase text-olive">FSSAI</p><p className="text-forest">{b.fssai || "—"}</p></div>
            </div>
            {(b.product_genres || []).length > 0 && <div><p className="text-xs text-brown/60 mb-1">Genres</p><div className="flex flex-wrap gap-1.5">{b.product_genres.map((g) => <span key={g} className="text-xs bg-sage/20 text-forest px-2 py-1 rounded-full">{g}</span>)}</div></div>}
            {(b.product_types || []).length > 0 && <div><p className="text-xs text-brown/60 mb-1">Types</p><div className="flex flex-wrap gap-1.5">{b.product_types.map((t) => <span key={t} className="text-xs bg-sand px-2 py-1 rounded-full text-brown">{t}</span>)}</div></div>}
            {(b.certifications || []).length > 0 && <div><p className="text-xs text-brown/60 mb-1">Certifications</p><div className="flex flex-wrap gap-1.5">{b.certifications.map((t) => <span key={t} className="text-xs bg-sand px-2 py-1 rounded-full text-brown">{t}</span>)}</div></div>}
          </div>
        </div>

        <div data-testid="catalogue-section">
          <BrandCatalogue onReload={loadStats} canEdit={canEdit} />
        </div>

        {brandRole === "owner" && (
          <div data-testid="team-section">
            <BrandTeam />
          </div>
        )}
      </main>
    </div>
  );
}
